import { skinnedColumn } from "./prewarm.ts";
import { emptyShadowMap } from "./atmosphere.ts";
import type { Matrix4, Vector4} from "three";
import { BoxGeometry, Color, InstancedMesh, Mesh, Scene, DepthTexture, LessEqualCompare, MeshBasicMaterial, NearestFilter, LinearFilter, OrthographicCamera, RedFormat, UnsignedIntType, Vector3, WebGLRenderTarget, type Camera, type Material, Object3D, type WebGLRenderer } from "three";

/**
 * Sombra do sol (Fase 13, G3): um mapa de profundidade ortográfico, uma cascata só, que segue a câmera e
 * é amostrado pelos nossos shaders (`sunShadow` em `chunks.ts`) com comparação em hardware e PCF. Os
 * materiais são nossos (nada de luzes do three, ADR 0025): o mapa, a matriz e os parâmetros entram nos
 * uniforms da atmosfera, divididos por referência.
 *
 * Quem projeta: tudo o que está visível e opaco, menos o que pede `userData.noShadow` (céu, mar, capim:
 * o capim tem posição no shader e seria desenhado errado pelo material de profundidade; o chão) e os
 * corpos com esqueleto que não pedem `userData.sunCaster` (as criaturas: dezenas de corpos animados
 * dobravam o custo do quadro no estresse; elas ficam nas sombras-blob, os heróis projetam).
 */

/** Tamanho do mapa por tier (0 desliga; o Baixo fica nas sombras-blob). */
export const SUN_SHADOW_SIZE = { baixo: 0, medio: 1024, alto: 2048 } as const;

/**
 * Meia-largura (m) da área coberta, à frente da câmera; o centro fica `ahead` m na direção do olhar
 * (no chão), e a sombra some nos últimos 10 % da borda.
 */
export const SUN_SHADOW_AREA = { half: 42, ahead: 22, depth: 240 } as const;

/** Força (quanto do sol a sombra tira) e o desvio na direção da normal (m por texel). */
export const SUN_SHADOW_STYLE = { strength: 0.9, normalBias: 1.6 } as const;

export interface SunShadowUniforms {
  uSunShadowMap: { value: DepthTexture };
  uSunShadowMatrix: { value: Matrix4 };
  /** x: tamanho do texel em uv; y: desvio na normal (m); z: força; w: ligado (1) ou não (0). */
  uSunShadowParams: { value: Vector4 };
}


const hidden: Object3D[] = [];
const hooked: [Object3D, Object3D["onBeforeRender"], Object3D["onAfterRender"]][] = [];
const noop = (): void => {};
// Os ganchos padrão (o protótipo, que não faz nada): só os objetos com ganchos próprios são trocados.
const plainBefore = Object3D.prototype.onBeforeRender;
const plainAfter = Object3D.prototype.onAfterRender;

/**
 * Prepara o passe da sombra: esconde quem não projeta e desliga os ganchos de desenho (os de objeto
 * escrevem uniforms no material deles e receberiam o de profundidade; os da cena medem o quadro
 * principal, e a sombra é contada à parte).
 */
function hideNonCasters(scene: Scene): void {
  hidden.length = 0;
  hooked.length = 0;
  scene.traverseVisible((o) => {
    if (o.onBeforeRender !== plainBefore || o.onAfterRender !== plainAfter) {
      hooked.push([o, o.onBeforeRender, o.onAfterRender]);
      o.onBeforeRender = noop;
      o.onAfterRender = noop;
    }
    if (o === scene) return;
    const material = (o as { material?: Material | Material[] }).material;
    const transparent = Array.isArray(material) ? material.some((m) => m.transparent) : material?.transparent === true;
    const skinned = (o as { isSkinnedMesh?: boolean }).isSkinnedMesh === true && o.userData["sunCaster"] !== true;
    if (skinned || o.userData["noShadow"] === true || transparent || (o as { isPoints?: boolean }).isPoints || (o as { isLine?: boolean }).isLine || (o as { isSprite?: boolean }).isSprite) {
      o.visible = false;
      hidden.push(o);
    }
  });
}

function restore(): void {
  for (const o of hidden) o.visible = true;
  for (const [o, before, after] of hooked) {
    o.onBeforeRender = before;
    o.onAfterRender = after;
  }
  hidden.length = 0;
  hooked.length = 0;
}

export class SunShadow {
  private target: WebGLRenderTarget | undefined;
  private size = 0;
  private readonly camera = new OrthographicCamera(-1, 1, 1, -1, 1, 2);
  private readonly caster = new MeshBasicMaterial({ colorWrite: false });
  private readonly center = new Vector3();
  private readonly forward = new Vector3();
  private readonly light = new Vector3();
  private readonly right = new Vector3();
  private readonly up = new Vector3();
  private readonly uniforms: SunShadowUniforms;
  /** Draw calls do último passe da sombra (o `stats()` os mostra à parte da cena). */
  calls = 0;
  /** Triângulos do último passe da sombra. */
  triangles = 0;
  private skipped = 0;
  private warmed = false;
  private readonly lastLight = new Vector3();

  constructor(uniforms: SunShadowUniforms) {
    this.uniforms = uniforms;
    this.caster.name = "sombra-do-sol";
  }

  /**
   * Desenha o mapa para a câmera do quadro e a direção da luz (`lightDir`: do chão para o sol, unitária),
   * com o tamanho do tier (0 desliga). Sol abaixo do horizonte: a sombra some aos poucos.
   */
  update(renderer: WebGLRenderer, scene: Scene, view: Camera, lightDir: Vector3, size: number, every = 1): void {
    const p = this.uniforms.uSunShadowParams.value;
    if (size <= 0 || lightDir.y <= 0.005) {
      p.w = 0;
      this.calls = 0;
      // Desligada (opção ou tier): o mapa sai da memória da GPU; o sol baixo só apaga.
      if (size <= 0) this.release();
      return;
    }
    this.ensure(size);
    // Primeira vez ligada: compila as variantes de uma vez (em paralelo onde houver), sem esperar por elas
    // (esperar deixaria o primeiro quadro sem sombra conforme o relógio da compilação: sondas instáveis).
    if (!this.warmed) {
      this.warmed = true;
      void this.prewarm(renderer).catch((error: unknown) => console.warn("pré-compilação da sombra", error));
    }
    // No Médio, o mapa refaz a cada `every` quadros (a sombra de quem anda atrasa um quadro) — menos
    // quando a luz mudou (a fase do dia, uma sonda), que pede o mapa novo já.
    const still = this.lastLight.distanceToSquared(lightDir) < 1e-10;
    this.lastLight.copy(lightDir);
    if (still && this.calls > 0 && p.w > 0 && ++this.skipped < every) return;
    this.skipped = 0;
    const half = SUN_SHADOW_AREA.half;
    // Centro: à frente da câmera, no plano horizontal.
    view.getWorldDirection(this.forward);
    this.forward.y = 0;
    if (this.forward.lengthSq() < 1e-6) this.forward.set(0, 0, -1);
    this.forward.normalize();
    this.center.setFromMatrixPosition(view.matrixWorld).addScaledVector(this.forward, SUN_SHADOW_AREA.ahead);
    this.light.copy(lightDir).normalize();
    const texel = (2 * half) / size;
    const cam = this.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.near = 1;
    cam.far = SUN_SHADOW_AREA.depth;
    cam.up.set(0, 1, 0);
    if (Math.abs(this.light.y) > 0.99) cam.up.set(0, 0, 1);
    cam.position.copy(this.center).addScaledVector(this.light, SUN_SHADOW_AREA.depth * 0.5);
    cam.lookAt(this.center);
    cam.updateMatrixWorld();
    // Estável: a câmera da luz anda de texel em texel nos eixos dela (a borda da sombra não tremula).
    const e = cam.matrixWorld.elements;
    this.right.set(e[0]!, e[1]!, e[2]!);
    this.up.set(e[4]!, e[5]!, e[6]!);
    const px = cam.position.dot(this.right);
    const py = cam.position.dot(this.up);
    cam.position.addScaledVector(this.right, Math.round(px / texel) * texel - px).addScaledVector(this.up, Math.round(py / texel) * texel - py);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    this.uniforms.uSunShadowMatrix.value.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    // A sombra some com o sol baixo demais (o mapa rasante só daria listras).
    const low = Math.min(1, (lightDir.y - 0.005) / 0.04);
    p.set(1 / size, texel * SUN_SHADOW_STYLE.normalBias, SUN_SHADOW_STYLE.strength * low, 1);
    this.uniforms.uSunShadowMap.value = this.target!.depthTexture!;

    const previous = { target: renderer.getRenderTarget(), override: scene.overrideMaterial, autoClear: renderer.autoClear, background: scene.background };
    hideNonCasters(scene);
    try {
      scene.overrideMaterial = this.caster;
      scene.background = null;
      renderer.setRenderTarget(this.target!);
      // O `render` limpa (autoClear): a profundidade nasce no fundo.
      renderer.autoClear = true;
      const before = renderer.info.render.calls;
      const beforeTriangles = renderer.info.render.triangles;
      renderer.render(scene, cam);
      // O three conta os desenhos (`info.render.frame`) e refaz o esqueleto de cada SkinnedMesh uma vez
      // por desenho: com o passe da luz, os ossos subiam duas vezes por quadro, e a segunda subida, com a
      // textura já em uso no mesmo quadro, travava a GPU (ANGLE sobre Metal) com dezenas de criaturas.
      // A volta do contador faz o desenho principal reaproveitar os ossos deste passe (o mesmo quadro).
      renderer.info.render.frame--;
      this.calls = renderer.info.render.calls - before;
      this.triangles = renderer.info.render.triangles - beforeTriangles;
    } finally {
      restore();
      scene.overrideMaterial = previous.override;
      scene.background = previous.background;
      renderer.setRenderTarget(previous.target);
      renderer.autoClear = previous.autoClear;
    }
  }

  private ensure(size: number): void {
    if (this.target && this.size === size) return;
    this.target?.dispose();
    const depth = new DepthTexture(size, size, UnsignedIntType);
    depth.compareFunction = LessEqualCompare;
    // Comparação com filtro linear: cada amostra já é um PCF 2×2 em hardware.
    depth.minFilter = LinearFilter;
    depth.magFilter = LinearFilter;
    // A cor nunca é escrita (`colorWrite: false`), mas o alvo pede uma: um canal de 8 bits (4 MB no Alto, não 16).
    this.target = new WebGLRenderTarget(size, size, { depthBuffer: true, depthTexture: depth, format: RedFormat, minFilter: NearestFilter, magFilter: NearestFilter });
    this.target.texture.name = "sombra-do-sol";
    this.size = size;
  }

  /**
   * Compila de antemão as variantes do material de profundidade (simples, em lote, em lote com cor e com
   * esqueleto): sem isso, o primeiro objeto de cada tipo a projetar compilava no meio da luta (engasgo).
   */
  private prewarm(renderer: WebGLRenderer): Promise<unknown> {
    const scene = new Scene();
    const skinned = skinnedColumn(this.caster).mesh;
    const box = new BoxGeometry(0.1, 0.1, 0.1);
    const colored = new InstancedMesh(box, this.caster, 1);
    colored.setColorAt(0, new Color(1, 1, 1));
    scene.add(new Mesh(box, this.caster), new InstancedMesh(box, this.caster, 1), colored, skinned);
    for (const o of scene.children) o.frustumCulled = false;
    // Num alvo (a saída linear, como o passe de verdade): no canvas a chave do programa seria outra.
    const target = new WebGLRenderTarget(1, 1);
    const previous = renderer.getRenderTarget();
    renderer.setRenderTarget(target);
    try {
      return renderer.compileAsync(scene, this.camera).finally(() => {
        box.dispose();
        target.dispose();
      });
    } finally {
      renderer.setRenderTarget(previous);
    }
  }

  /** Solta o mapa (volta o vazio nos uniforms); a próxima vez que ligar, ele nasce de novo. */
  private release(): void {
    if (!this.target) return;
    this.uniforms.uSunShadowMap.value = emptyShadowMap();
    this.target.dispose();
    this.target = undefined;
    this.size = 0;
  }

  dispose(): void {
    this.release();
    this.caster.dispose();
    this.uniforms.uSunShadowMap.value = emptyShadowMap();
    this.uniforms.uSunShadowParams.value.w = 0;
  }
}
